import React, { useState, useEffect } from 'react';
import cookieConsentConfig from './Cookieconsentconfig';

/**
 * Unified Cookie Consent Interface Modal
 * Automatically syncs with centralized configuration
 */
export default function CookieConsentInterface({
  onConfirm,
  onClose,
  initial = {}
}) {
  const [config, setConfig] = useState(cookieConsentConfig.getConfig());
  const [showModal, setShowModal] = useState(true);
  const [showPreferences, setShowPreferences] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [preferences, setPreferences] = useState(() => {
    const prefs = {};
    const cats = config?.categories || [];
    cats.forEach(c => {
      prefs[c.id] = initial[c.id] ?? config?.preferences?.[c.id] ?? (c.required || c.alwaysOn || !!c.defaultOn);
    });
    return prefs;
  });

  // Subscribe to config changes
  useEffect(() => {
    const unsubscribe = cookieConsentConfig.subscribe((newConfig) => {
      setConfig(newConfig);
      if (newConfig?.categories) {
        setPreferences(prev => {
          const next = { ...prev };
          newConfig.categories.forEach(c => {
            if (next[c.id] === undefined) {
              next[c.id] = (c.required || c.alwaysOn) ? true : !!c.defaultOn;
            }
          });
          return next;
        });
      }
    });
    return unsubscribe;
  }, []);

  // Listen for global open event
  useEffect(() => {
    const handleOpen = () => {
      setShowModal(true);
      setShowPreferences(false);
    };

    window.addEventListener('open-cookie-preferences', handleOpen);
    window.addEventListener('open-cookie-consent', handleOpen);

    return () => {
      window.removeEventListener('open-cookie-preferences', handleOpen);
      window.removeEventListener('open-cookie-consent', handleOpen);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleToggle = (key) => {
    const cat = (config?.categories || []).find(c => c.id === key);
    if (cat && (cat.required || cat.alwaysOn)) return;
    setPreferences(prev => {
      const updated = { ...prev, [key]: !prev[key] };
      // Update centralized config in real-time
      cookieConsentConfig.updatePreferences(updated);
      return updated;
    });
  };

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  const handleConfirm = (prefs) => {
    // Update centralized config
    cookieConsentConfig.updatePreferences(prefs);
    setShowModal(false);
    if (onConfirm) onConfirm(prefs);

    // Notify widget of changes
    window.dispatchEvent(new CustomEvent('cookie-config-updated', { detail: prefs }));
  };

  if (!showModal) return null;

  // Get styles from config
  const styles = cookieConsentConfig.getWidgetStyles();

  const getBorderRadius = () => {
    if (config.containerShape === 'rounded') return 12;
    if (config.containerShape === 'square') return 4;
    return 8;
  };

  const primaryBg = '#2563eb';
  const primaryColor = '#ffffff';
  const secondaryBg = '#eaf4ff';

  const btnBase = {
    padding: '10px 20px',
    borderRadius: config.buttonStyle === 'pill' ? 9999 :
      (config.buttonStyle === 'square' ? 4 : 8),
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'all 120ms ease'
  };

  const allowAllStyle = {
    ...btnBase,
    background: primaryBg,
    color: primaryColor,
    boxShadow: '0 6px 18px rgba(37,99,235,0.12)'
  };

  const prefsBtnStyle = {
    ...btnBase,
    background: primaryBg,
    color: '#fff',
    boxShadow: '0 6px 18px rgba(37,99,235,0.12)'
  };

  const cancelBtnStyle = {
    ...btnBase,
    background: '#ffffff',
    color: '#111827',
    border: '1px solid #e5e7eb'
  };

  const manageStyle = {
    ...btnBase,
    background: secondaryBg,
    color: primaryBg,
    boxShadow: '0 4px 12px rgba(37,99,235,0.08)'
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl my-8"
        style={{
          borderRadius: getBorderRadius(),
          boxShadow: styles.container.boxShadow,
          background: '#ffffff',
          maxWidth: '920px',
          margin: '2rem auto'
        }}
      >
        {/* Header */}
        <div
          className="border-b border-gray-200 p-6 flex items-start justify-between"
          style={{
            background: config.backgroundColor,
            borderBottom: '1px solid #e5e7eb',
            padding: '1.5rem'
          }}
        >
          <div className="flex items-center gap-3">
            <h2 style={{
              fontSize: 20,
              fontWeight: 600,
              color: '#1f2937',
              margin: 0
            }}>
              {showPreferences ? 'Manage Consent Preferences' : 'Privacy Preference Center'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 24,
              color: '#6b7280',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {!showPreferences ? (
            <div>
              <div dangerouslySetInnerHTML={{ __html: config.content }} />
              <p style={{ color: '#4b5563', lineHeight: '1.6', marginTop: '1rem' }}>
                When you visit any website, it may store or retrieve information on your browser,
                mostly in the form of cookies. You can choose not to allow some types of cookies.
                Click Manage Preferences to change defaults.
              </p>
              {config.privacyUrl ? (
                <a
                  href={config.privacyUrl}
                  style={{ color: primaryBg, display: 'inline-block', marginTop: '0.5rem', textDecoration: 'none' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Statement
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => { }}
                  style={{ background: 'transparent', border: 'none', color: primaryBg, padding: 0, marginTop: '0.5rem', cursor: 'pointer' }}
                >
                  Privacy Statement
                </button>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: '1.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    const declined = {};
                    (config?.categories || []).forEach(c => {
                      declined[c.id] = !!(c.required || c.alwaysOn);
                    });
                    setPreferences(declined);
                    handleConfirm(declined);
                  }}
                  style={cancelBtnStyle}
                >
                  Decline
                </button>
                <button
                  onClick={() => {
                    const accepted = {};
                    (config?.categories || []).forEach(c => {
                      accepted[c.id] = true;
                    });
                    setPreferences(accepted);
                    handleConfirm(accepted);
                  }}
                  style={allowAllStyle}
                >
                  Accept All
                </button>
                <button
                  onClick={() => setShowPreferences(true)}
                  style={manageStyle}
                >
                  Preferences
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                onClick={() => {
                  const all = {};
                  (config?.categories || []).forEach(c => {
                    all[c.id] = true;
                  });
                  setPreferences(all);
                  handleConfirm(all);
                }}
                style={{ ...allowAllStyle, marginBottom: '1rem' }}
              >
                Allow All
              </button>

              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: '1rem' }}>
                Consent Preferences
              </h3>

              <div style={{ display: 'grid', gap: 12 }}>
                {(config?.categories || []).map(category => (
                  <div
                    key={category.id}
                    style={{
                      border: '1px solid #e6e7eb',
                      borderRadius: 8,
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{
                      background: '#f9fafb',
                      padding: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <button
                        onClick={() => toggleSection(category.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        <span style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: '#111827'
                        }}>
                          {category.title || category.label || category.id}
                        </span>
                      </button>

                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        {category.required || category.alwaysOn ? (
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#059669', background: '#d1fae5', padding: '3px 8px', borderRadius: 6 }}>
                            Always On
                          </span>
                        ) : (
                          <>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>
                              {preferences[category.id] ? 'On' : 'Off'}
                            </span>
                            <button
                              aria-pressed={preferences[category.id]}
                              onClick={() => handleToggle(category.id)}
                              style={{
                                width: 46,
                                height: 26,
                                borderRadius: 16,
                                background: preferences[category.id] ? '#10b981' : '#e5e7eb',
                                border: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: 3,
                                cursor: 'pointer',
                                transition: 'background 120ms ease'
                              }}
                            >
                              <div style={{
                                width: 20,
                                height: 20,
                                borderRadius: 10,
                                background: '#fff',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                transform: preferences[category.id] ? 'translateX(20px)' : 'translateX(0)',
                                transition: 'transform 120ms ease'
                              }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {expandedSections[category.id] && (
                      <div style={{ padding: 12 }}>
                        <p style={{ color: '#374151', margin: 0 }}>
                          {category.description}
                        </p>
                        {category.cookiesUrl ? (
                          <a href={category.cookiesUrl} style={{ color: primaryBg, display: 'inline-block', marginTop: 8, textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">Cookies Details</a>
                        ) : (
                          <button type="button" onClick={() => { }} style={{ background: 'transparent', border: 'none', color: primaryBg, padding: 0, marginTop: 8, cursor: 'pointer' }}>Cookies Details</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '1.5rem',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 12
              }}>
                <button
                  onClick={handleClose}
                  style={{ ...cancelBtnStyle, padding: '8px 16px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirm(preferences)}
                  style={{ ...prefsBtnStyle, padding: '8px 16px' }}
                >
                  Confirm My Choices
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #eef2f7',
          padding: 12,
          background: '#f8fafc',
          textAlign: 'left',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          <strong>
            Powered by{' '}
            <a href="https://www.whitehats.tech/" target="_blank" rel="noopener noreferrer" style={{ color: '#2356c4', textDecoration: 'none' }}>
              whitehats
            </a>
          </strong>
        </div>
      </div>
    </div>
  );
}